import { useCallback, useEffect, useState } from "react";
import type { MemeOverviewDto } from "@/api/types";
import { ApiError, fetchMemeOverview } from "@/api/client";
import StatTile from "@/components/StatTile";
import MemePositionCard from "@/components/MemePositionCard";
import PnlHistoryChart from "@/components/PnlHistoryChart";
import MemeScanLog from "@/components/MemeScanLog";

const POLL_INTERVAL_MS = 10000;

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

const KILL_SWITCH_REASON_LABEL: Record<string, string> = {
  sleeve_disabled: "sleeve disabled in settings",
  wallet_not_configured: "wallet not configured",
  no_capital_allocated: "no capital allocated",
  daily_loss: "daily loss limit",
};

interface MemeSleevePanelProps {
  onUnauthorized: () => void;
}

export default function MemeSleevePanel({ onUnauthorized }: MemeSleevePanelProps) {
  const [overview, setOverview] = useState<MemeOverviewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await fetchMemeOverview();
      setOverview(result);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load meme sleeve data");
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => {
    void refresh();
    const interval = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  if (loading && !overview) {
    return (
      <div className="rounded-xl border border-line bg-panel p-8 text-center font-mono text-xs text-ink-faint">
        Loading meme sleeve…
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="rounded-xl border border-sell-dim/50 bg-sell/5 p-8 text-center font-mono text-xs text-sell">
        {error}
      </div>
    );
  }

  if (!overview) return null;

  const { killSwitch } = overview;

  return (
    <div className="space-y-6">
      {killSwitch.triggered && (
        <div className="rounded-xl border border-sell-dim/50 bg-sell/5 p-3 font-mono text-xs text-sell">
          Meme sleeve buys are blocked — {KILL_SWITCH_REASON_LABEL[killSwitch.reason ?? ""] ?? killSwitch.reason}.
          Existing open positions are still monitored and exited normally.
        </div>
      )}

      {!overview.dryRun && !overview.walletConfigured && (
        <div className="rounded-xl border border-gold-dim/50 bg-gold/5 p-3 font-mono text-xs text-gold">
          Live mode but no MEME_WALLET_PRIVATE_KEY configured — the sleeve cannot trade until a wallet is set.
        </div>
      )}

      {overview.walletConfigured && overview.walletBalanceError && (
        <div className="rounded-xl border border-gold-dim/50 bg-gold/5 p-3 font-mono text-xs text-gold">
          Could not read on-chain wallet balance — {overview.walletBalanceError}
        </div>
      )}

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile
          label="Sleeve Kill Switch"
          value={killSwitch.triggered ? "Blocked" : "Armed"}
          sublabel={killSwitch.triggered ? KILL_SWITCH_REASON_LABEL[killSwitch.reason ?? ""] : undefined}
          tone={killSwitch.triggered ? "sell" : "turquoise"}
        />
        <StatTile
          label="Daily Loss"
          value={`${killSwitch.dailyLossPercent.toFixed(1)}%`}
          sublabel={`limit ${killSwitch.thresholdPercent}%`}
          tone={killSwitch.reason === "daily_loss" ? "sell" : "default"}
        />
        <StatTile
          label="Open Positions"
          value={`${overview.positions.open.length} / ${overview.maxConcurrentPositions}`}
        />
        <StatTile
          label="Remaining Capital"
          value={`$${overview.remainingCapitalUsd.toFixed(2)}`}
          tone="turquoise"
        />
        <StatTile
          label="Allocated Capital"
          value={`$${overview.allocatedCapitalUsd.toFixed(2)}`}
        />
        <StatTile
          label="Wallet Balance"
          value={overview.walletBnbBalance !== null ? `${overview.walletBnbBalance.toFixed(4)} BNB` : "—"}
          sublabel={
            overview.walletBnbBalance === null
              ? overview.walletConfigured
                ? "unavailable"
                : "no wallet configured"
              : [
                  overview.walletBnbBalanceUsd !== null ? `$${overview.walletBnbBalanceUsd.toFixed(2)}` : null,
                  overview.walletAddress ? truncateAddress(overview.walletAddress) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
          }
          tone="gold"
        />
      </section>

      <MemeScanLog scanLog={overview.scanLog} />

      <section>
        <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
          Open Meme Positions{overview.positions.open.length > 0 ? ` (${overview.positions.open.length})` : ""}
        </h3>
        {overview.positions.open.length === 0 ? (
          <div className="mt-3 rounded-xl border border-line bg-panel p-8 text-center font-mono text-xs text-ink-faint">
            No open meme positions.
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            {overview.positions.open.map((position) => (
              <MemePositionCard key={position.id} position={position} />
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">Recently Closed</h3>
          {overview.positions.closed.length === 0 ? (
            <div className="mt-3 rounded-xl border border-line bg-panel p-8 text-center font-mono text-xs text-ink-faint">
              No closed meme positions yet.
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {overview.positions.closed.slice(0, 6).map((position) => (
                <MemePositionCard key={position.id} position={position} />
              ))}
            </div>
          )}
        </section>
        <section>
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
            Meme Sleeve Daily PnL (14d)
          </h3>
          <div className="mt-3 rounded-xl border border-line bg-panel p-4 shadow-panel">
            <PnlHistoryChart history={overview.pnlHistory} />
          </div>
        </section>
      </div>
    </div>
  );
}
